import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "../../lib/api";
import DashboardLayout from "../../components/layout/DashboardLayout";
import StatusBadge from "../../components/StatusBadge";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { formatDateTime } from "../../lib/format";

export default function AdminSupport() {
    const [tickets, setTickets] = useState([]);
    const [filter, setFilter] = useState("all");
    const [activeId, setActiveId] = useState(null);
    const [active, setActive] = useState(null);
    const [reply, setReply] = useState("");

    const load = async () => {
        const params = filter === "all" ? {} : { status: filter };
        const { data } = await api.get("/admin/tickets", { params });
        setTickets(data || []);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

    useEffect(() => {
        if (!activeId) { setActive(null); return; }
        api.get(`/admin/tickets/${activeId}`).then(({ data }) => setActive(data));
    }, [activeId]);

    const sendReply = async () => {
        if (!reply.trim()) return;
        try {
            await api.post(`/admin/tickets/${activeId}/reply`, { message: reply });
            setReply("");
            const { data } = await api.get(`/admin/tickets/${activeId}`);
            setActive(data);
            await load();
            toast.success("Reply sent");
        } catch (err) { toast.error(apiErrorMessage(err)); }
    };

    const updateTicket = async (status, priority) => {
        try {
            await api.patch(`/admin/tickets/${activeId}`, { status, priority });
            const { data } = await api.get(`/admin/tickets/${activeId}`);
            setActive(data);
            await load();
            toast.success("Ticket updated");
        } catch (err) { toast.error(apiErrorMessage(err)); }
    };

    return (
        <DashboardLayout role="admin">
            <div className="mb-8 flex items-center justify-between flex-wrap gap-4" data-testid="admin-support-page">
                <div>
                    <div className="label-mono mb-3">/ Support</div>
                    <h1 className="font-heading font-black text-3xl lg:text-4xl tracking-tighter">Tickets</h1>
                </div>
                <Select value={filter} onValueChange={setFilter}>
                    <SelectTrigger className="bg-avx-surface border-avx-line h-11 w-44"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="open">Open</SelectItem>
                        <SelectItem value="in_progress">In progress</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            <div className="grid lg:grid-cols-[1fr_1.5fr] gap-6">
                <div className="space-y-2 max-h-[80vh] overflow-y-auto">
                    {tickets.length === 0 ? (
                        <div className="panel p-8 text-center text-avx-muted text-sm">No tickets.</div>
                    ) : tickets.map((t) => (
                        <button key={t.id} onClick={() => setActiveId(t.id)} className={`w-full text-left panel p-4 hover:border-avx-blue transition-colors ${activeId === t.id ? "border-avx-blue" : ""}`} data-testid={`admin-ticket-item-${t.id}`}>
                            <div className="flex justify-between items-start mb-2">
                                <h3 className="font-heading font-bold text-sm">{t.subject}</h3>
                                <StatusBadge status={t.status} />
                            </div>
                            <div className="font-mono text-[10px] text-avx-muted uppercase tracking-[0.18em] flex justify-between">
                                <span>{t.user_email}</span>
                                <span>{formatDateTime(t.updated_at)}</span>
                            </div>
                        </button>
                    ))}
                </div>
                <div className="panel p-6 min-h-[300px]">
                    {!active ? (
                        <div className="text-avx-muted text-sm">Select a ticket.</div>
                    ) : (
                        <div>
                            <div className="flex justify-between items-start mb-4 pb-4 border-b border-avx-line">
                                <div>
                                    <h2 className="font-heading font-bold text-xl">{active.subject}</h2>
                                    <div className="text-xs font-mono text-avx-muted mt-1">{active.user_name} · {active.user_email}</div>
                                </div>
                                <div className="flex gap-2">
                                    <Select value={active.priority} onValueChange={(v) => updateTicket(active.status, v)}>
                                        <SelectTrigger className="bg-avx-bg border-avx-line h-9 w-32"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="low">Low</SelectItem>
                                            <SelectItem value="medium">Medium</SelectItem>
                                            <SelectItem value="high">High</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <Select value={active.status} onValueChange={(v) => updateTicket(v, active.priority)}>
                                        <SelectTrigger className="bg-avx-bg border-avx-line h-9 w-36"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="open">Open</SelectItem>
                                            <SelectItem value="in_progress">In progress</SelectItem>
                                            <SelectItem value="closed">Closed</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="space-y-3 mb-6 max-h-[400px] overflow-y-auto">
                                {(active.messages || []).map((m) => (
                                    <div key={m.id} className={`p-3 rounded-sm border ${m.author_role === "admin" ? "border-avx-blue/40 bg-avx-blue/5" : "border-avx-line bg-avx-surface/40"}`}>
                                        <div className="flex justify-between mb-1">
                                            <span className="text-xs font-mono">{m.author_name} · <span className="text-avx-muted">{m.author_role}</span></span>
                                            <span className="text-[10px] font-mono text-avx-muted">{formatDateTime(m.created_at)}</span>
                                        </div>
                                        <p className="text-sm whitespace-pre-wrap">{m.message}</p>
                                    </div>
                                ))}
                            </div>
                            <div className="space-y-3">
                                <Textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={3} placeholder="Reply to the customer…" className="bg-avx-bg border-avx-line" data-testid="admin-reply-textarea" />
                                <button onClick={sendReply} className="btn-primary px-5 py-2 text-xs font-mono uppercase tracking-[0.2em] rounded-sm" data-testid="admin-reply-btn">
                                    Send reply
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </DashboardLayout>
    );
}
